import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  MutableRefObject,
} from 'react';
import { isEqual } from 'lodash';
import noop from 'utils/noop';
import { EditableQuery, CompleteQuery, Combinator, ESIndex, ESIndexConfig } from '../types';
import { createBlankQuery } from '../utils/createBlankQuery';
import { createBlankCriteria } from '../utils/createBlankCriteria';
import { getDefaultQuery } from '../utils/getDefaultQuery';
import { applyQueryTransformations } from '../schemas/utils/transformations';

export type QueryBuilderState = {
  defaultCombinator: Combinator;
  isReadOnly?: boolean;
  isLoading?: boolean;
  setIsReadOnly: Dispatch<SetStateAction<boolean>>;
  query: EditableQuery;
  setQuery: Dispatch<SetStateAction<EditableQuery>>;
  recentQueryId: string;
  setToLatestId: Dispatch<SetStateAction<string>>;
  resetQueryId: () => void;
};

// Useful values for query builder form consumers to access
export type ReturnedQueryBuilderState = {
  query: EditableQuery;
  isDirty: boolean;
};

export type GetQueryStateRefType = MutableRefObject<() => ReturnedQueryBuilderState>;

export type QueryBuilderStateContext = ESIndexConfig & QueryBuilderState;

type QueryStateContextProps = ESIndexConfig & {
  defaultCombinator?: Combinator;
  isReadOnly?: boolean;
  isLoading?: boolean;
  onSetEditableQuery?: (query: EditableQuery) => void;
  passedQuery?: CompleteQuery;
  getQueryState?: GetQueryStateRefType;
};

export const queryStateDefaultState: QueryBuilderStateContext = {
  defaultCombinator: Combinator.AND,
  esIndex: ESIndex.UserAndEvent,
  isReadOnly: false,
  isLoading: false,
  setIsReadOnly: noop,
  query: createBlankQuery({
    searchQueries: [createBlankCriteria({ combinator: Combinator.AND })],
  }),
  setQuery: noop,
  setToLatestId: noop,
  resetQueryId: noop,
  recentQueryId: '0',
};

const QueryStateContext = createContext(queryStateDefaultState);

export const useQueryStateContext = () => useContext(QueryStateContext);

export const QueryStateContextProvider: FC<PropsWithChildren<QueryStateContextProps>> = ({
  children,
  defaultCombinator,
  esIndex = ESIndex.UserAndEvent,
  onSetEditableQuery,
  passedQuery,
  isLoading = false,
  getQueryState,
  ...otherProps
}) => {
  const combinator = defaultCombinator ?? Combinator.AND;
  const [isReadOnly, setIsReadOnly] = useState(!!otherProps?.isReadOnly);
  const defaultQuery = useMemo(
    () => getDefaultQuery(combinator, esIndex, passedQuery),
    [combinator, esIndex, passedQuery],
  );
  const [query, setQuery] = useState<EditableQuery>(defaultQuery);
  const [recentQueryId, setToLatestId] = useState(() => {
    const firstCriteriaId = query.searchQueries[0].id;
    return firstCriteriaId;
  });

  const resetQueryId = useCallback(() => setToLatestId(query.searchQueries[0].id), [query]);
  const setEditableQuery = (value: SetStateAction<EditableQuery>) => {
    setQuery(value);
    const resolvedValue = typeof value === 'function' ? value(query) : value;
    onSetEditableQuery?.(resolvedValue);
  };

  const getQueryStateCallback = useCallback(() => {
    const parsedQuery = applyQueryTransformations(query);
    return {
      isDirty: !isEqual(query, defaultQuery),
      query: parsedQuery,
    } as ReturnedQueryBuilderState;
  }, [JSON.stringify(defaultQuery), JSON.stringify(query)]);

  const value = useMemo(
    () => ({
      ...otherProps,
      defaultCombinator: combinator,
      esIndex,
      isReadOnly,
      isLoading,
      setIsReadOnly,
      query,
      recentQueryId,
      resetQueryId,
      setToLatestId,
      setQuery: setEditableQuery,
    }),
    [
      combinator,
      esIndex,
      isReadOnly,
      isLoading,
      query,
      setQuery,
      setToLatestId,
      recentQueryId,
      resetQueryId,
    ],
  );

  useEffect(() => {
    // Assign the function to get current state to the ref
    if (getQueryState) {
      getQueryState.current = getQueryStateCallback;
    }
  }, [getQueryState, getQueryStateCallback]);

  return <QueryStateContext.Provider value={value}>{children}</QueryStateContext.Provider>;
};
